import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRightCircle, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtET, fmtUS, fmtTime, today } from "@/lib/api";
import { Field, Select } from "@/components/common";
import { useApp } from "@/context/AppContext";
import { PatientPicker } from "@/components/PatientPicker";

export const OTHER_SERVICE = "Other — Enter Manually";
export const STATUSES = ["Assigned", "In Progress", "Completed", "Cancelled"];

export function AssignmentDialog({ open, onClose, st, edit, canEditAll, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const { user } = useApp();
  const navMode = user.role === "employee";
  const services = st?.services || [];
  useEffect(() => {
    if (!open) return;
    if (edit) {
      const listed = services.includes(edit.service);
      setF({ athena_id: edit.athena_id, service: listed ? edit.service : OTHER_SERVICE, custom_service: listed ? "" : edit.service, employee_id: edit.employee_id, notes: edit.notes || "", status: edit.status, testing_date: edit.testing_date || edit.local_date });
    } else {
      setF({ athena_id: "", service: "", custom_service: "", employee_id: navMode ? user.id : st?.next_employee?.user_id || "", notes: "", testing_date: today(), patient_ref: "" });
    }
  }, [open, edit]); // eslint-disable-line react-hooks/exhaustive-deps
  const staff = st?.staff || [];
  const sug = st?.next_employee;
  const save = async () => {
    if (navMode && !edit && !f.patient_ref) return toast.error("Select a Patient ID from your assigned patients");
    if (!f.athena_id?.trim()) return toast.error("Patient Athena ID is required");
    if (!f.testing_date) return toast.error("Testing date is required");
    if (!f.service) return toast.error("Service is required");
    if (f.service === OTHER_SERVICE && !f.custom_service?.trim()) return toast.error("Enter Service Name is required");
    if (!f.employee_id) return toast.error("Select an employee");
    setBusy(true);
    try {
      if (edit) {
        const body = navMode ? { status: f.status, service: f.service, custom_service: f.custom_service, notes: f.notes, testing_date: f.testing_date } : canEditAll ? f : { status: f.status };
        await api.put(`/rotation/assignments/${edit.id}`, body);
        toast.success("Assignment updated");
      } else {
        await api.post("/rotation/assignments", { ...f, location_id: st.location.id, suggested_employee_id: sug?.user_id || null, confirmed: true });
        toast.success(navMode ? "Testing assignment saved" : "Assignment saved — rotation advanced");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
      if (e?.response?.status === 409) onSaved();
    } finally {
      setBusy(false);
    }
  };
  const ro = edit && !canEditAll;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="assignment-dialog">
        <DialogHeader><DialogTitle className="text-[#20336F] dark:text-blue-200">{edit ? "Edit Testing Assignment" : "Add Patient Testing Assignment"}</DialogTitle></DialogHeader>
        {!edit && !navMode && (
          <div className="flex items-center gap-3 rounded-xl bg-[#FFF5DF] p-3 text-sm dark:bg-white/5" data-testid="suggested-employee">
            <ArrowRightCircle size={18} className="text-amber-600" />
            <span>Next in rotation: <b data-testid="suggested-employee-name">{sug?.name || "No eligible employee right now"}</b></span>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          {navMode && !edit
            ? <Field label="Patient ID" required><PatientPicker value={f.patient_ref} onPick={(p) => setF({ ...f, patient_ref: p.id, athena_id: p.patient_id })} testid="assign-patient-picker" /></Field>
            : <Field label={navMode ? "Patient ID" : "Patient Athena ID"} required><input className="inp" disabled={ro || navMode} value={f.athena_id || ""} onChange={(e) => setF({ ...f, athena_id: e.target.value })} data-testid="assign-athena-input" autoFocus /></Field>}
          <Field label="Testing Date" required><input type="date" className="inp" value={f.testing_date || ""} onChange={(e) => setF({ ...f, testing_date: e.target.value })} data-testid="assign-testing-date" /></Field>
          <Field label="Service" required><Select value={f.service} onChange={(v) => setF({ ...f, service: v })} placeholder="Select service" testid="assign-service-select" options={[...services, OTHER_SERVICE]} /></Field>
          {f.service === OTHER_SERVICE && <Field label="Enter Service Name" required className="sm:col-span-2"><input className="inp" disabled={ro} value={f.custom_service || ""} onChange={(e) => setF({ ...f, custom_service: e.target.value })} data-testid="assign-custom-service-input" /></Field>}
          <Field label="Assigned Employee" required>
            <Select value={f.employee_id} disabled={navMode} onChange={(v) => setF({ ...f, employee_id: v })} placeholder="Select employee" testid="assign-employee-select"
              options={navMode ? [{ value: f.employee_id || user.id, label: edit?.employee_name || user.name }] : staff.map((s) => ({ value: s.id, label: `${s.name}${s.id === sug?.user_id ? " (suggested)" : ""}${s.in_rotation ? "" : " — not in rotation"}` }))} />
          </Field>
          <Field label="Location"><input className="inp" disabled value={edit?.location_name || st?.location?.name || ""} data-testid="assign-location" /></Field>
          {edit && <Field label="Status"><Select value={f.status} onChange={(v) => setF({ ...f, status: v })} testid="assign-status-select" options={STATUSES} /></Field>}
          <Field label="Date and Time"><div className="flex h-9 items-center gap-2 text-sm text-[var(--muted-ink)]"><Clock size={14} />{edit ? `${fmtUS(edit.local_date)} ${fmtTime(edit.local_time)} ET` : "Recorded automatically on save"}</div></Field>
          <Field label="Notes" className="sm:col-span-2"><textarea className="inp" rows={2} disabled={ro} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="assign-notes-input" /></Field>
        </div>
        {!edit && !navMode && f.employee_id && sug && f.employee_id !== sug.user_id && <p className="text-xs text-amber-700" data-testid="override-warning">Manual override — this will be recorded in the audit log and the rotation will stay on {sug.name}.</p>}
        {edit?.history?.length > 0 && (
          <div className="max-h-28 overflow-y-auto rounded-md bg-[var(--surface)] p-2 text-xs" data-testid="assignment-history">
            {edit.history.map((h, i) => <div key={i}><b>{fmtET(h.at)}</b> {h.by}: {Object.entries(h.changes).map(([k, v]) => `${k} ${v[0] ?? "—"} → ${v[1]}`).join("; ")}</div>)}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="assign-save-btn">{busy ? "Saving…" : edit ? "Save Changes" : "Confirm & Save Assignment"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
